import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Fab,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Visibility,
  VisibilityOff,
  LocationOn,
  ArrowBack,
  CheckCircle,
  Cancel,
  Schedule,
} from '@mui/icons-material';
import api from '../../services/api';

interface Listing {
  id: string;
  title: string;
  description: string;
  price: number;
  category: string;
  category_id: number;
  thumbnail_url: string;
  images: string[];
  location: string;
  status: 'active' | 'sold' | 'expired' | 'draft';
  views: number;
  created_at: string;
  updated_at: string;
  expires_at: string;
}

const MyListingsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  useEffect(() => {
    fetchMyListings();
  }, []);

  const fetchMyListings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/marketplace/my-listings');
      setListings(response.data.listings || response.data);
    } catch (error: any) {
      console.error('Error fetching my listings:', error);
      setError(error.response?.data?.detail || t('marketplace.myListings.fetchError'));
      
      // If unauthorized, redirect to marketplace with auth modal
      if (error.response?.status === 401) {
        navigate('/app/marketplace');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteListing = async () => {
    if (!selectedListing) return;

    setStatusUpdateLoading(true);
    try {
      const response = await api.delete(`/marketplace/listings/${selectedListing.id}`);
      console.log('Delete response:', response);
      
      // Update the listings state to remove the deleted item
      setListings(prev => prev.filter(l => l.id !== selectedListing.id));
      
      // Close dialog and clear selection
      setDeleteDialogOpen(false);
      setSelectedListing(null);
    } catch (error: any) {
      console.error('Delete error:', error);
      setError(error.response?.data?.detail || t('marketplace.myListings.deleteError'));
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleStatusChange = async (listingId: string, newStatus: string) => {
    setStatusUpdateLoading(true);
    try {
      await api.patch(`/marketplace/listings/${listingId}/status`, {
        status: newStatus
      });
      
      // Update the listing in the state
      setListings(prev => prev.map(l => 
        l.id === listingId ? { ...l, status: newStatus as any } : l
      ));
      
      handleMenuClose();
    } catch (error: any) {
      setError(error.response?.data?.detail || t('marketplace.myListings.statusError'));
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, listing: Listing) => {
    setAnchorEl(event.currentTarget);
    setSelectedListing(listing);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    if (!deleteDialogOpen) {
      setSelectedListing(null);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'sold':
        return 'primary';
      case 'expired':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle fontSize="small" />;
      case 'sold':
        return <CheckCircle fontSize="small" />;
      case 'expired':
        return <Cancel fontSize="small" />;
      case 'draft':
        return <Schedule fontSize="small" />;
      default:
        return undefined;
    }
  };

  const getPlaceholderImage = () => {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjE1MCIgeT0iMTUwIiBzdHlsZT0iZmlsbDojYWFhO2ZvbnQtd2VpZ2h0OmJvbGQ7Zm9udC1zaXplOjE5cHg7Zm9udC1mYW1pbHk6QXJpYWwsSGVsdmV0aWNhLHNhbnMtc2VyaWY7ZG9taW5hbnQtYmFzZWxpbmU6Y2VudHJhbCI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
  };

  const getFilteredListings = () => {
    switch (selectedTab) {
      case 0: // All
        return listings;
      case 1: // Active
        return listings.filter(l => l.status === 'active');
      case 2: // Sold
        return listings.filter(l => l.status === 'sold');
      case 3: // Expired
        return listings.filter(l => l.status === 'expired');
      default:
        return listings;
    }
  };

  const getTabCount = (status?: string) => {
    if (!status) return listings.length;
    return listings.filter(l => l.status === status).length;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>{t('marketplace.myListings.loading')}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/app/marketplace')}
          >
            {t('marketplace.backToMarketplace')}
          </Button>
          <Typography variant="h4">
            {t('marketplace.myListings.title')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/app/marketplace/create')}
        >
          {t('marketplace.myListings.createNew')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, v) => setSelectedTab(v)}
          variant="fullWidth"
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{t('marketplace.myListings.tabs.all')}</span>
                <Chip label={getTabCount()} size="small" color="primary" />
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{t('marketplace.myListings.tabs.active')}</span>
                <Chip label={getTabCount('active')} size="small" color="success" />
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{t('marketplace.myListings.tabs.sold')}</span>
                <Chip label={getTabCount('sold')} size="small" />
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{t('marketplace.myListings.tabs.expired')}</span>
                <Chip label={getTabCount('expired')} size="small" color="error" />
              </Box>
            } 
          />
        </Tabs>
      </Paper>

      {getFilteredListings().length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {t('marketplace.myListings.noListings')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            {selectedTab === 0 
              ? t('marketplace.myListings.startSelling')
              : t('marketplace.myListings.noListingsInCategory')
            }
          </Typography>
          {selectedTab === 0 && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/app/marketplace/create')}
            >
              {t('marketplace.myListings.createFirst')}
            </Button>
          )}
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {getFilteredListings().map((listing) => (
            <Card key={listing.id} sx={{ display: 'flex' }}>
              <CardMedia
                component="img"
                sx={{ width: 200, height: 150, objectFit: 'cover' }}
                image={listing.thumbnail_url || getPlaceholderImage()}
                alt={listing.title}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <CardContent sx={{ flex: '1 0 auto' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography component="h5" variant="h6">
                        {listing.title}
                      </Typography>
                      <Typography variant="h5" color="primary" sx={{ mt: 1 }}>
                        {formatPrice(listing.price)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        icon={getStatusIcon(listing.status)}
                        label={t(`marketplace.status.${listing.status}`)}
                        color={getStatusColor(listing.status) as any}
                        size="small"
                      />
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, listing)}
                        disabled={statusUpdateLoading}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                    {listing.category && (
                      <Chip
                        label={listing.category}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {listing.location && (
                      <Chip
                        icon={<LocationOn />}
                        label={listing.location}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    <Chip
                      icon={<Visibility />}
                      label={`${listing.views} ${t('marketplace.listing.views')}`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {listing.description.length > 150 
                      ? `${listing.description.substring(0, 150)}...` 
                      : listing.description
                    }
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => navigate(`/app/marketplace/listing/${listing.id}`)}
                  >
                    {t('marketplace.myListings.view')}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => navigate(`/app/marketplace/edit/${listing.id}`)}
                  >
                    {t('marketplace.myListings.edit')}
                  </Button>
                </CardActions>
              </Box>
            </Card>
          ))}
        </Box>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedListing?.status === 'active' && (
          <MenuItem 
            onClick={() => handleStatusChange(selectedListing.id, 'sold')}
            disabled={statusUpdateLoading}
          >
            <CheckCircle sx={{ mr: 1 }} fontSize="small" />
            {t('marketplace.myListings.markAsSold')}
          </MenuItem>
        )}
        {selectedListing?.status === 'sold' && (
          <MenuItem 
            onClick={() => handleStatusChange(selectedListing.id, 'active')}
            disabled={statusUpdateLoading}
          >
            <VisibilityOff sx={{ mr: 1 }} fontSize="small" />
            {t('marketplace.myListings.reactivate')}
          </MenuItem>
        )}
        <MenuItem 
          onClick={() => navigate(`/app/marketplace/edit/${selectedListing?.id}`)}
        >
          <Edit sx={{ mr: 1 }} fontSize="small" />
          {t('marketplace.myListings.edit')}
        </MenuItem>
        <MenuItem 
          onClick={() => {
            setDeleteDialogOpen(true);
            setAnchorEl(null); // Close menu but keep selectedListing
          }}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} fontSize="small" />
          {t('marketplace.myListings.delete')}
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedListing(null);
        }}
      >
        <DialogTitle>{t('marketplace.myListings.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('marketplace.myListings.deleteConfirmMessage', { title: selectedListing?.title || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setSelectedListing(null);
          }}>
            {t('marketplace.myListings.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteListing} 
            color="error"
            disabled={statusUpdateLoading}
            startIcon={statusUpdateLoading && <CircularProgress size={20} />}
          >
            {t('marketplace.myListings.confirmDelete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAB */}
      <Fab
        color="primary"
        aria-label="add listing"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => navigate('/app/marketplace/create')}
      >
        <Add />
      </Fab>
    </Container>
  );
};

export default MyListingsPage;